'use client'

import React, { useEffect, useRef, useState } from 'react'

export interface ProgressItem {
  label: string
  value: number
}

interface AnimatedProgressBarsProps {
  items: ProgressItem[]
  className?: string
  barClassName?: string
}

export function AnimatedProgressBars({
  items,
  className = 'w-full max-w-sm space-y-4 text-left',
  barClassName = 'h-1.5 w-full overflow-hidden rounded-full bg-neutral-200',
}: AnimatedProgressBarsProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isInView, setIsInView] = useState(false)

  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) setIsInView(true)
      },
      { threshold: 0.2, rootMargin: '0px 0px -40px 0px' },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={containerRef} className={className}>
      {items.map((item, _index) => (
        <div key={item.label}>
          {/* Label and % on one row so long labels (e.g. Global Distribution Reliability) don't overlap % on mobile */}
          <div className="font-poppins mb-0.5 flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0 text-xs font-medium text-neutral-700">
            <span className="min-w-0 flex-1">{item.label}</span>
            <span className="shrink-0">{item.value}%</span>
          </div>
          <div className="relative flex items-center">
            <div className={barClassName + ' min-h-0 flex-1'}>
              <div
                className="h-full rounded-full bg-[#FF0000] transition-[width] duration-700 ease-out"
                style={{ width: isInView ? `${item.value}%` : '0%' }}
              />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}
